import React, { useState } from 'react'
import { useMutation, useQueries, useQuery } from '@tanstack/react-query'
import { Modal } from '../../../common/Modal'
import { ModalBody } from '../../../common/Modal/ModalBody'
import { ModalHeader } from '../../../common/Modal/ModalHeader'
import { ModalFooter } from '../../../common/Modal/ModalFooter'
import { ToastType } from '../../../common/Toast/toast-type'
import { useToastNotifications } from '../../../../hooks/useToastNotification'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { useAuthenticatedUserId } from '../../../../hooks/useAuthenticatedUserId'
import { Button, ButtonVariant } from '../../../common/Button'
import { DeleteAccountModalContent } from './DeleteAccountModalContent'
import {
  canRemoveUserFromSociety,
  societyQueries,
} from '../../../../api/societies'
import { unitQueries } from '../../../../api/units'
import { deleteUser } from '../../../../api/users'
import { useAuth } from '../../../../hooks/useAuth'

export enum DeleteAccountStage {
  FIRST = 0,
  SECOND = 1,
  LAST = 2,
}

interface DeleteAccountModalProps {
  show: boolean
  close: () => void
}

export const DeleteAccountModal = ({
  show,
  close,
}: DeleteAccountModalProps): React.ReactNode => {
  const { setToastNotification } = useToastNotifications()
  const userId = useAuthenticatedUserId() as string
  const { translate } = useAppTranslation()
  const [stage, setStage] = useState<DeleteAccountStage>(
    DeleteAccountStage.FIRST
  )
  const [password, setPassword] = useState('')
  const { data: societies } = useQuery(societyQueries.getUserSocieties(userId))

  const unitsQuery = useQueries({
    queries:
      societies?.map((society) =>
        unitQueries.getUnitsPerSociety(society._id)
      ) || [],
  })

  const fetchingUnits =
    unitsQuery.flatMap((query) => query.isLoading || []).length > 0
  const units = unitsQuery.flatMap((query) => query.data || [])

  const societiesPreventingAccountDeletion =
    societies?.filter(
      (_society) =>
        !canRemoveUserFromSociety(
          userId,
          _society,
          units.filter((unit) => unit.societyId === _society._id)
        )
    ) || []
  const canNotDeleteAccount = societiesPreventingAccountDeletion.length > 0

  const showSuccessMessage = (): void => {
    setToastNotification(
      ToastType.SUCCESS,
      translate('deleteAccountModal.flashMessage.deleteAccountSuccess')
    )
  }

  const showErrorMessage = (): void => {
    setToastNotification(
      ToastType.DANGER,
      translate('deleteAccountModal.flashMessage.confirmPasswordFailure')
    )
  }

  const onClose = (): void => {
    setStage(DeleteAccountStage.FIRST)
    close()
  }

  const { logout } = useAuth()

  const { mutate: mutateDeleteUser, isPending: deletingUser } = useMutation({
    mutationFn: (variables: { userId: string; password: string }) =>
      deleteUser(variables.userId, { password: variables.password }),
    onSuccess: () => {
      showSuccessMessage()
      logout()
      onClose()
    },
    onError: () => {
      showErrorMessage()
    },
  })

  const onDeleteClick = (): void => {
    mutateDeleteUser({ password, userId })
  }

  const updateStage = (): void => {
    setStage(stage + 1)
  }

  const onChangePassword = (confirmedPassword: string): void => {
    setPassword(confirmedPassword)
  }

  const onForwardButtonClick = (): void => {
    if (stage === DeleteAccountStage.LAST) {
      onDeleteClick()
    } else {
      updateStage()
    }
  }

  return (
    <Modal show={show} size="md">
      {{
        header: (
          <ModalHeader onClose={onClose}>
            {translate('deleteAccountModal.title')}
          </ModalHeader>
        ),
        body: (
          <ModalBody>
            <DeleteAccountModalContent
              stage={stage}
              societiesPreventingAccountDeletion={
                societiesPreventingAccountDeletion
              }
              loading={fetchingUnits}
              onChangePassword={onChangePassword}
              password={password}
            />
          </ModalBody>
        ),
        footer: (
          <ModalFooter>
            <Button
              onClick={onClose}
              label={translate('common.actions.cancel')}
              variant={ButtonVariant.DEFAULT}
            />
            <Button
              loading={fetchingUnits || deletingUser}
              variant={
                stage === DeleteAccountStage.LAST
                  ? ButtonVariant.DANGER
                  : ButtonVariant.PRIMARY
              }
              label={
                stage === DeleteAccountStage.LAST
                  ? translate('deleteAccountModal.title')
                  : translate('common.actions.continue')
              }
              disabled={
                (stage === DeleteAccountStage.LAST && password.length === 0) ||
                fetchingUnits ||
                canNotDeleteAccount
              }
              onClick={onForwardButtonClick}
            />
          </ModalFooter>
        ),
      }}
    </Modal>
  )
}
